import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/privacy.css";
import Footerlanding from "../landing/Footerlanding";
import Navbarlanding from "../landing/Navbarlanding";
import { parse } from "tldts";

const PrivacyPolicy = () => {
  const [company, setCompany] = useState({
    name: "Your Company",
    logo: "",
    favicon: "",
    domain: "",
    subdomain: "",
    email:"",
  });
  const [fetchError, setFetchError] = useState("");

  const getDomainInfo = () => {
    const hostname = window.location.hostname;
    const parsed = parse(hostname);
    return {
      domain: parsed.domainWithoutSuffix || "",
      subdomain: parsed.subdomain || "",
    };
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { domain } = getDomainInfo();
        const response = await axios.get("https://payyug.in/api/get-company", {
          params: { domain },
        });
        const data = response.data;

        if (data && Object.keys(data).length > 0) {
          setCompany((prev) => ({
            ...prev,
            name: data.name || prev.name,
            logo: data.logo || prev.logo,
            favicon: data.favicon || prev.favicon,
            domain: data.domain || prev.domain,
            subdomain: data.subdomain || prev.subdomain,
            email: data.email || prev.email,
          }));

          if (data.favicon) {
            const link =
              document.querySelector("link[rel*='icon']") ||
              document.createElement("link");
            link.type = "image/png";
            link.rel = "icon";
            link.href = `https://payyug.in/${
              data.favicon.startsWith("images/")
                ? data.favicon.slice(7)
                : data.favicon
            }`;
            document.head.appendChild(link);
          }
        } else {
          setFetchError("Company info not found.");
        }
      } catch (err) {
        console.error("Company fetch failed:", err);
        setFetchError("Failed to fetch company info.");
      }
    };

    fetchCompany();
  }, []);

  const supportEmail = company?.email || "support@yourdomain.com";
  const contactEmail = company?.email || "contact@yourdomain.com";
  return (
    <div className="privacy-page">
      <Navbarlanding />

      <div className="privacy-wrapper">
        <h1>Privacy Policy – {company.name}</h1>
        <p className="last-updated">Effective Date: January 2026</p>

        {fetchError && <p className="error-text">{fetchError}</p>}

        <section>
          <p>
            {company.name} (“we”, “our”, “us”) operates the website{" "}
            {company.domain || "yourdomain.com"} and is committed to protecting
            the privacy and personal information of its users.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, protect, and
            disclose information when you access or use our digital banking
            software platform.
          </p>
          <p>
            By using {company.domain || "yourdomain.com"}, you agree to the
            practices described in this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>
          <p>We may collect and process the following types of information:</p>

          <h3>a. Personal Information</h3>
          <ul>
            <li>Full name</li>
            <li>Mobile number</li>
            <li>Email address</li>
            <li>Residential address</li>
            <li>Date of birth (if required)</li>
          </ul>

          <h3>b. Identity & KYC Information</h3>
          <ul>
            <li>Government-issued identification details</li>
            <li>Photographs and documents submitted for verification</li>
          </ul>

          <h3>c. Financial Information</h3>
          <ul>
            <li>Account-related details</li>
            <li>Loan application information</li>
            <li>Transaction data (as applicable)</li>
          </ul>

          <h3>d. Technical Information</h3>
          <ul>
            <li>IP address</li>
            <li>Browser type and device information</li>
            <li>Log files and usage data</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>To provide and manage our services</li>
            <li>To process account and loan applications</li>
            <li>To verify identity and complete KYC requirements</li>
            <li>To communicate service updates and support responses</li>
            <li>To improve platform functionality and user experience</li>
            <li>To comply with legal, regulatory, and compliance obligations</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Sharing & Disclosure</h2>
          <p>We do not sell or rent your personal information.</p>
          <p>Your information may be shared only:</p>
          <ul>
            <li>
              With authorized service providers or financial partners strictly
              for service delivery
            </li>
            <li>When required by law, regulation, or government authorities</li>
            <li>
              To protect the rights, safety, and security of {company.name} and
              its users
            </li>
          </ul>
          <p>All third parties are required to maintain strict confidentiality.</p>
        </section>

        <section>
          <h2>4. Data Security</h2>
          <p>
            We implement appropriate technical, administrative, and physical
            security measures to protect your data, including:
          </p>
          <ul>
            <li>SSL encryption</li>
            <li>Secure servers and restricted access</li>
            <li>Regular system monitoring</li>
          </ul>
          <p>
            Despite our best efforts, no digital platform can guarantee 100%
            security.
          </p>
        </section>

        <section>
          <h2>5. Cookies & Tracking Technologies</h2>
          <p>
            {company.name} may use cookies and similar technologies to:
          </p>
          <ul>
            <li>Enhance website functionality</li>
            <li>Analyze traffic and usage trends</li>
            <li>Improve user experience</li>
          </ul>
          <p>
            You may disable cookies through your browser settings, though some
            features may not function properly.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>We retain your personal information only for:</p>
          <ul>
            <li>As long as necessary to provide services</li>
            <li>Compliance with legal and regulatory requirements</li>
          </ul>
          <p>
            Once the purpose is fulfilled, data is securely deleted or
            anonymized.
          </p>
        </section>

        <section>
          <h2>7. User Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Request correction or updates</li>
            <li>Request deletion (subject to legal obligations)</li>
            <li>Withdraw consent where applicable</li>
          </ul>
          <p>
            Requests can be sent to: <br />
            📧 <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites.{" "}
            {company.name} is not responsible for the privacy practices or
            content of external websites.
          </p>
        </section>

        <section>
          <h2>9. Children’s Privacy</h2>
          <p>
            Our services are not intended for individuals under the age of 18.
            We do not knowingly collect personal information from minors.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Privacy Policy</h2>
          <p>
            {company.name} reserves the right to update or modify this Privacy
            Policy at any time. Changes will be effective immediately upon
            posting on the website. Users are encouraged to review this page
            periodically.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions or concerns regarding this Privacy Policy
            or your data, please contact us at:
          </p>
          <p>
            {/* 📧 <a href={`mailto:${supportEmail}`}>{supportEmail}</a> <br /> */}
            📧 <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </p>
        </section>

        <section>
          <h2>Disclaimer</h2>
          <p>
            {company.name} is a digital banking software platform. All services
            are provided subject to applicable laws, eligibility criteria, and
            partner approvals.
          </p>
        </section>
      </div>

      <Footerlanding />
    </div>
  );
};

export default PrivacyPolicy;
